import { visibleMessages } from './agent-visibility'

/**
 * Filters a channel conversation segment's participant list through the shared per-zone
 * agent-visibility rule (`agent-visibility.ts`).
 *
 * `ConversationSummary.participants` is a plain `string[]`, not something with a `sender` field,
 * so it cannot be handed to {@link visibleMessages} directly. Wrapping and unwrapping keeps the
 * ONE filtering rule as the single source of truth rather than growing a second
 * `!hidden.includes(...)` that could drift from it.
 *
 * **As of 2026-08-31, the backend never actually populates `participants` for a CHANNEL-kind
 * conversation** — `ConversationAssembler.rail`/`.group` hand back `List.of()` for every channel
 * segment, because (per `ChannelConversation`'s own javadoc, and ARCHITECTURE.md §26/D23) a
 * channel's membership changes freely, so there is no fixed participant set to BE its identity,
 * unlike a DIRECT conversation. Verified live against the dev zone: a 28-message channel segment
 * came back with `"participants": []`. So on real data today this function is always called with
 * an empty array and always returns one — the design spec's "What already exists" table assumed
 * this field was already populated for channels, and it never has been.
 *
 * It is kept, and tested here with synthetic data (exactly like `collapse.test.ts` and
 * `agent-visibility.test.ts` test their pure functions without a live app), because it is still
 * the CORRECT wiring if a participant list is ever added to that DTO, and because removing it
 * would drop the one place the spec's "apply visibleMessages to the conversation participants
 * shown" line is honoured literally. The boundary the spec actually cares about — that an agent
 * hidden on the Messages page must not reappear because a channel's conversation view was opened —
 * is realised TODAY by `ChannelDetail`'s "Speakers" line in `ChannelsPage.tsx` instead, which
 * derives real names from `GET /messages/index?channel=` (already channel-filterable) rather than
 * from this always-empty field.
 */
export function visibleParticipants(participants: string[], hidden: string[]): string[] {
  return visibleMessages(
    participants.map((name) => ({ sender: name })),
    hidden,
  ).map((entry) => entry.sender)
}
